import * as Sentry from '@sentry/react';

export interface SentryConfig {
    /**
     * Sentry DSN for the widget
     */
    dsn: string;
    /**
     * Environment name (e.g., 'development', 'staging', 'production')
     */
    environment: string;
    /**
     * Performance monitoring sample rate (0.0 to 1.0)
     * @default 0.1
     */
    tracesSampleRate?: number;
    /**
     * Session replay sample rate (0.0 to 1.0)
     * @default 0.1
     */
    replaysSessionSampleRate?: number;
    /**
     * Errors to ignore (regex patterns)
     * @default [/invalid JSON from Parse/i]
     */
    ignoreErrors?: Array<string | RegExp>;
}

/**
 * Initialize Sentry with widget-specific configuration
 * Automatically disables Sentry in development and on localhost
 */
export default function sentryInit(config: SentryConfig): void {
    if (process.env.NODE_ENV === 'development') {
        console.log('disabling sentry in development');
        return;
    }

    // for compiled mode
    if (window.location.hostname === 'localhost') {
        console.log('disabling sentry on localhost');
        return;
    }

    Sentry.init({
        dsn: config.dsn,
        integrations: [
            new Sentry.BrowserTracing(),
            new Sentry.Replay(),
            new Sentry.GlobalHandlers({
                // Do not capture global errors outside of Sentry context
                // NOTE: Please wrap your code in a <Sentry.ErrorBoundary> when using this
                onerror: false,
                onunhandledrejection: false,
            }),
        ],
        // Performance Monitoring
        tracesSampleRate: config.tracesSampleRate ?? 0.1,
        environment: config.environment,
        replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0.1,
        ignoreErrors: config.ignoreErrors ?? [/invalid JSON from Parse/i], // TODO: Once we migrate to AWS, remove me
    });
}

/**
 * Set Sentry account tag for better error tracking
 */
export function setSentryAccount(accountName: string): void {
    Sentry.setTag('account', accountName);
}

/**
 * Set Sentry user information for better error tracking
 */
export function setSentryUser(userId: string, userEmail: string): void {
    Sentry.setUser({
        id: userId,
        email: userEmail,
    });
}

const SENTRY_TIMEOUT_MS = 1000;
const SENTRY_FAILURE_THRESHOLD = 3;
const SENTRY_FAILURE_TIMEOUT_MS = 30_000;

let sentryFailures = 0;
let sentryDisabledUntil = 0;

/**
 * Capture an exception to Sentry with circuit breaker logic
 * Includes timeout and failure tracking to prevent blocking the widget
 *
 * @param e - The error to capture
 * @param additionalTags - Optional additional tags to include
 * @returns Promise that resolves immediately (fire and forget)
 */
export async function captureSentryException(
    e: Error | unknown,
    additionalTags?: Record<string, string>
): Promise<void> {
    console.error('Captured Exception e', e);

    // Circuit breaker: skip Sentry if it's been failing
    if (Date.now() < sentryDisabledUntil) {
        console.warn('Sentry disabled due to failures, skipping');
        return;
    }

    // Fire and forget with timeout
    Promise.race([
        Sentry.captureException(e, {
            tags: {
                href: window.location?.href,
                ...(additionalTags || {}),
            },
        }),
        new Promise((_, reject) =>
            setTimeout(
                () => reject(new Error('Sentry timeout')),
                SENTRY_TIMEOUT_MS
            )
        ),
    ])
        .then(() => {
            sentryFailures = 0;
        })
        .catch((err) => {
            sentryFailures++;

            if (sentryFailures >= SENTRY_FAILURE_THRESHOLD) {
                sentryDisabledUntil = Date.now() + SENTRY_FAILURE_TIMEOUT_MS;
                console.error(
                    `Sentry failed ${sentryFailures} times, disabling for ${
                        SENTRY_FAILURE_TIMEOUT_MS / 1000
                    }s`
                );
                sentryFailures = 0;
            } else {
                console.error('Sentry error:', err.message);
            }
        });

    // Immediately resolve so that this call isn't blocking
    return Promise.resolve();
}
